import type { CollectionVersionRef } from "../../shared/references/contentReferences";
import type { ApiSchema } from "../apiSchema";
import { jsonBody, requestJson, withQuery } from "../http/httpClient";
import { readArray, readBoolean, readObject, readString, type JsonObject } from "../http/jsonDecoding";
import { readCollectionVersionRef, readCollectionVersionSummary, readStringList, type CollectionVersionSummary } from "./contentDecoders";

/**
 * Corpora of a project: list, create, and import documents as new corpus versions
 * (FR-CORPUS-1 to 3; `docs/components/content.md` D-6, D-7). Creating and importing need
 * `propose_change` in the project.
 */

export type ImportMode = ApiSchema<"ImportMode">;

/**
 * A segmentation configuration as the user wrote it (`dmw-segmentation/1`). The server
 * checks it and answers `segmentation_configuration_invalid` with the reason.
 */
export type SegmentationConfigurationInput = JsonObject;

export interface CorpusSummary {
  readonly corpusId: string;
  readonly name: string;
  readonly latestVersion: CollectionVersionRef;
  /** The segmentation configuration of `latestVersion`. */
  readonly segmentation: SegmentationConfigurationInput;
  /** The kinds of segment below the document, shallowest first: what a search can be limited to (F141). */
  readonly segmentTypes: readonly string[];
  /** Whether `latestVersion` declares other ways to write its keys (`key_forms`), so its keys have a short form (F136). */
  readonly writesShortKeys: boolean;
}

export interface CorpusImportResult {
  readonly version: CollectionVersionRef;
  /** False if nothing changed; `version` is then the current one. */
  readonly createdVersion: boolean;
  readonly addedCount: number;
  readonly modifiedCount: number;
  readonly removedCount: number;
  readonly unchangedCount: number;
  readonly segmentationChanged: boolean;
}

/** The media type of a JSON Lines import body, one `{key, text, metadata}` per line. */
const JSON_LINES_MEDIA_TYPE = "application/x-ndjson";

export function listCorpora(projectId: string, signal?: AbortSignal): Promise<readonly CorpusSummary[]> {
  return requestJson({
    method: "GET",
    path: `/projects/${encodeURIComponent(projectId)}/corpora`,
    signal,
    decodeResponse: (body) => readArray(body, "corpora", (item) => decodeCorpus(readObject(item, "corpus"))),
  });
}

/** A corpus's versions, newest first. */
export function listCorpusVersions(corpusId: string, signal?: AbortSignal): Promise<readonly CollectionVersionSummary[]> {
  return requestJson({
    method: "GET",
    path: `/corpora/${encodeURIComponent(corpusId)}/versions`,
    signal,
    decodeResponse: (body) => readArray(body, "corpus versions", (item) => readCollectionVersionSummary(item, "corpus version")),
  });
}

/** Create a corpus; its first version holds no documents. */
export function createCorpus(
  projectId: string,
  name: string,
  segmentation: SegmentationConfigurationInput,
): Promise<CorpusSummary> {
  const body: Omit<ApiSchema<"CorpusCreationBody">, "segmentation"> & { segmentation: SegmentationConfigurationInput } = {
    name,
    segmentation,
  };
  return requestJson({
    method: "POST",
    path: `/projects/${encodeURIComponent(projectId)}/corpora`,
    body: jsonBody(body),
    decodeResponse: (response) => decodeCorpus(readObject(response, "corpus")),
  });
}

/**
 * Import a JSON Lines file as one new corpus version. `expectedVersion` is the corpus
 * version the user saw; a newer one makes the import fail with `content_version_conflict`.
 * A `newSegmentation` replaces the corpus's in the same version and re-segments every
 * document, also those not in the file.
 */
export function importJsonLines(
  corpusId: string,
  file: Blob,
  mode: ImportMode,
  expectedVersion: CollectionVersionRef,
  newSegmentation: SegmentationConfigurationInput | null,
): Promise<CorpusImportResult> {
  return requestJson({
    method: "POST",
    path: withQuery(`/corpora/${encodeURIComponent(corpusId)}/imports/json-lines`, {
      mode,
      expected_version: expectedVersion.commit,
      segmentation: newSegmentation === null ? null : JSON.stringify(newSegmentation),
    }),
    body: { kind: "file", content: file, contentType: JSON_LINES_MEDIA_TYPE },
    decodeResponse: decodeImportResult,
  });
}

function decodeCorpus(corpus: JsonObject): CorpusSummary {
  const segmentation = readObject(corpus["segmentation"], "segmentation");
  const keyForms = segmentation["key_forms"];
  return {
    corpusId: readString(corpus, "corpus_id"),
    name: readString(corpus, "name"),
    latestVersion: readCollectionVersionRef(corpus, "latest_version"),
    segmentation,
    segmentTypes: readStringList(corpus, "segment_types"),
    writesShortKeys: Array.isArray(keyForms) && keyForms.length > 0,
  };
}

function decodeImportResult(body: unknown): CorpusImportResult {
  const result = readObject(body, "import result");
  return {
    version: readCollectionVersionRef(result, "version"),
    createdVersion: readBoolean(result, "created_version"),
    addedCount: readStringList(result, "added_item_ids").length,
    modifiedCount: readStringList(result, "modified_item_ids").length,
    removedCount: readStringList(result, "removed_item_ids").length,
    unchangedCount: readStringList(result, "unchanged_item_ids").length,
    segmentationChanged: readBoolean(result, "segmentation_changed"),
  };
}
